import React from 'react'
import { Helmet } from 'react-helmet-async'
import { relativeToAbsoluteUrl } from '#src/services/navigation'
import { getSellableUrl, normalizeSellablePricing } from '#src/services/sellable'
import { SellableDetailsFragment, SellableType, MediaFileFragment } from '#src/generated/graphql'
import PageTitle from '#src/components/PageTitle'
import config from '#src/config'

interface SellableMetaDataProps {
  sellable: SellableDetailsFragment
}

const SellableMetaData: React.SFC<SellableMetaDataProps> = ({ sellable }) => {
  /**
   * Page Titles
   *
   * Callaway M4 Driver | Curated.com
   * 132' Northcoast - Bahamas | Curated.com
   */
  const pageTitle =
    sellable.type === SellableType.PRODUCT
      ? sellable.title
      : `${sellable.title}${
          sellable.serviceLocation && sellable.serviceLocation.displayTitle !== null
            ? ` - ${sellable.serviceLocation.displayTitle}`
            : ''
        }`

  /**
   * Open graph image and twitter card
   *
   * Prefer the default variant and fallback to the sellable image
   */
  let sellableImages: MediaFileFragment[] = []
  if (sellable.images && sellable.images.length > 0) {
    sellableImages = sellable.images
  }

  const primarySellableImageUrl = sellableImages && sellableImages[0] && sellableImages[0].url

  /**
   * Canonical
   *
   * Generate the proper sellable url from the helper
   */
  const sellableUrl = getSellableUrl(sellable)
  const fullCanonical = `${config.get('consumer.protocol')}://${config.get(
    'consumer.host'
  )}${sellableUrl}`

  /**
   * Description / preview
   *
   * Until we have more coverage on our descriptions, use boiler plate copy so there aren't
   * just random strings indexed from the page.
   */
  const sellableDescription =
    sellable.descriptionText ||
    `Get free advice from a live expert about the ${
      sellable.title
    }. Free shipping & lowest price guarantee!`

  const sellablePricing = normalizeSellablePricing(sellable)

  const isOutOfStock =
    sellable.variationMatrix !== null &&
    sellable.variationMatrix.outOfStockSellables.find(
      outOfStockSellable => outOfStockSellable.id === sellable.id
    ) !== undefined

  // See: https://developers.google.com/search/docs/data-types/product
  const linkedData = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: pageTitle,
    image: sellableImages.map(image => image.url),
    description: sellableDescription,
    brand: sellable.brand && {
      '@type': 'Thing',
      name: sellable.brand.displayName
    },
    offers: {
      '@type': 'Offer',
      sku: sellable.id,
      url: relativeToAbsoluteUrl(sellableUrl),
      price: sellablePricing && `${sellablePricing.price.amount}`,
      priceCurrency: sellablePricing && `${sellablePricing.price.currency}`,
      availability: isOutOfStock ? 'http://schema.org/OutOfStock' : 'http://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: 'Curated'
      }
    }
  }

  return (
    <>
      <PageTitle title={pageTitle} />
      <Helmet>
        {(sellable.adHoc || !sellable.active) && <meta name="robots" content="noindex" />}
        {primarySellableImageUrl && <meta property="og:image" content={primarySellableImageUrl} />}
        {primarySellableImageUrl && (
          <meta property="twitter:card" content={primarySellableImageUrl} />
        )}
        <meta property="og:url" content={fullCanonical} />
        <link rel="canonical" href={fullCanonical} />
        <meta property="og:description" content={sellableDescription} />
        <meta property="description" content={sellableDescription} />
        <meta property="og:type" content="website" />
        <meta property="Cecchi" content="MacNaughton" />
        <script type="application/ld+json">{`${JSON.stringify(linkedData)}`}</script>
      </Helmet>
    </>
  )
}

export default SellableMetaData
